import { Fragment, memo, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import {
  backgroundPaddingPx,
  isItalicTextStyle,
  textBackgroundSx,
  textEmphasisSx,
  textShadowInkPad,
  textShadowSx,
  strokeWidthPx,
  textStrokeSx,
} from "./textStyle";
import {
  expandCompactPhraseInText,
  maxTextBackgroundPadding,
  listLineHighlightRuns,
} from "./textHighlights";
import {
  INLINE_IMAGE_CAP_EM,
  isInlineImageRun,
  listActiveInlineReplacements,
  listLineInlineRuns,
} from "./textInlineReplacements";
import {
  isVerticalTextOrientation,
  isVerticalUpTextOrientation,
} from "./textOrientation";
import { measureFontStrut, wrapTextVertical } from "../instance/fitTextInBox";

const sourceTextLines = (value) => {
  const lines = String(value || "").split("\n");
  return lines.length > 0 ? lines : [""];
};

/**
 * What the user's selection currently points at inside `root`:
 * - { kind: "range", phrase, start, end }  a phrase is selected
 * - { kind: "caret", offset }              the selection collapsed to a caret
 * - { kind: "none" }                       nothing is selected
 * - null                                   outside `root`, not ours
 *
 * Offsets index the source text. They are only meaningful in the editor, where
 * newlines are literal text nodes: Range.toString() concatenates text nodes
 * only, so the wrapped read-only paint would drop the breaks between lines.
 */
const readSelectionState = (root) => {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return { kind: "none" };
  const range = selection.getRangeAt(0);
  if (!root?.contains(range.commonAncestorContainer)) return null;
  const probe = document.createRange();
  probe.selectNodeContents(root);
  probe.setEnd(range.startContainer, range.startOffset);
  const offset = probe.toString().length;
  const text = root.innerText ?? "";
  const raw = String(selection.toString() || "");
  const phrase = raw.trim();
  if (phrase) {
    // Skip whatever whitespace the trim dropped off the front.
    const start = offset + (raw.length - raw.trimStart().length);
    return { kind: "range", phrase, start, end: start + phrase.length, text };
  }
  return { kind: "caret", offset, text };
};

const glyphStrokeLayers = (sx) => {
  const { WebkitTextStroke, paintOrder, ...rest } = sx;
  if (!WebkitTextStroke) return { outer: rest, inner: null };
  return {
    outer: {
      ...rest,
      WebkitTextStroke: "0px transparent",
      textDecorationColor: "currentColor",
    },
    inner: {
      WebkitTextStroke,
      paintOrder,
      textDecoration: "none",
    },
  };
};

const GlyphSpan = ({ sx, children, paint = "all", ...rest }) => {
  if (paint === "background") {
    return (
      <Box component="span" sx={sx} {...rest}>
        {children}
      </Box>
    );
  }
  const { outer, inner } = glyphStrokeLayers(sx);
  if (!inner) {
    return (
      <Box component="span" sx={outer} {...rest}>
        {children}
      </Box>
    );
  }
  return (
    <Box component="span" sx={outer} {...rest}>
      <Box component="span" sx={inner}>
        {children}
      </Box>
    </Box>
  );
};

// Canvas produce paints the pill from this far above the baseline down to 1em
// below that (textStyle paintTextBackground), whatever the font.
const PILL_ASCENT_EM = 0.78;

const cssBaselineCache = new Map();

const isFontReady = ({ fontFamily, fontSize, fontWeight, italic }) => {
  try {
    return document.fonts?.check
      ? document.fonts.check(
          `${italic ? "italic " : ""}${fontWeight} ${fontSize}px "${fontFamily}"`
        )
      : true;
  } catch (_error) {
    return true;
  }
};

/**
 * Where the first baseline of a line box falls, measured from the top of the
 * block, for the font the editor is about to lay out.
 *
 * Derived from canvas metrics this lands ~2px off: fontBoundingBox and the
 * layout engine's strut do not read the same tables, so the block is probed in
 * the DOM instead and the answer cached per font.
 */
const measureCssFirstBaseline = ({
  fontFamily,
  fontSize,
  fontWeight,
  italic,
  lineAdvance,
  fallback,
}) => {
  if (typeof document === "undefined" || !(Number(fontSize) > 0)) {
    return fallback;
  }
  const key = [fontFamily, fontSize, fontWeight, italic, lineAdvance].join("|");
  const cached = cssBaselineCache.get(key);
  if (cached != null) return cached;
  const host = document.createElement("div");
  host.style.cssText = [
    "position:absolute",
    "visibility:hidden",
    "left:-9999px",
    "top:0",
    "white-space:pre",
    `font-style:${italic ? "italic" : "normal"}`,
    `font-weight:${fontWeight}`,
    `font-size:${fontSize}px`,
    `font-family:"${fontFamily}"`,
    `line-height:${lineAdvance}px`,
  ].join(";");
  const marker = document.createElement("span");
  marker.style.cssText =
    "display:inline-block;width:0;height:0;vertical-align:baseline";
  host.append(document.createTextNode("Hg"), marker);
  document.body.appendChild(host);
  const baseline =
    marker.getBoundingClientRect().top - host.getBoundingClientRect().top;
  document.body.removeChild(host);
  const next = Number.isFinite(baseline) ? baseline : fallback;
  // Probed against a fallback face the answer is wrong for the real one, so it
  // is only kept once the font has loaded.
  if (isFontReady({ fontFamily, fontSize, fontWeight, italic })) {
    cssBaselineCache.set(key, next);
  }
  return next;
};

// The read-only paint keeps every pill in one absolute layer under the glyphs.
// Editing cannot: one span carries both, and a fragmented inline box paints
// background and text per fragment, so the pill of line 2 lands on top of the
// glyphs of line 1. Lifting the glyphs into the positioned layer restores the
// order — positioned content paints after every in-flow background.
//
// The pill span also carries a shrunken font so its background area is exactly
// 1em (see pillFontSize); the glyphs inside have to come back to the real size.
const EditGlyphs = ({ fontSize, children }) => (
  <Box
    component="span"
    sx={{
      position: "relative",
      ...(Number(fontSize) > 0 ? { fontSize: `${fontSize}px` } : {}),
    }}
  >
    {children}
  </Box>
);

const HighlightedSourceText = ({
  text,
  lineRuns,
  runTypeSx,
  glyphFontSize,
}) => {
  if (!lineRuns)
    return <EditGlyphs fontSize={glyphFontSize}>{text}</EditGlyphs>;
  return lineRuns.map((runs, lineIndex) => (
    <Fragment key={`src-line-${lineIndex}`}>
      {lineIndex > 0 ? "\n" : null}
      {runs.map((run, runIndex) => (
        <GlyphSpan key={`src-run-${lineIndex}-${runIndex}`} sx={runTypeSx(run)}>
          <EditGlyphs fontSize={glyphFontSize}>{run.text}</EditGlyphs>
        </GlyphSpan>
      ))}
    </Fragment>
  ));
};

// Frozen against parent re-renders so React does not reset the caret.
const EditingFittedText = memo(
  function EditingFittedText({
    text,
    highlights,
    highlightPhrases,
    orientation,
    align,
    sharedType,
    background,
    lineBackground,
    lineAdvance,
    totalHeight,
    topOffset = 0,
    glyphFontSize,
    color,
    opacity,
    runTypeSx,
    onCommit,
    onSelectionChange,
    onLeave,
  }) {
    const vertical = isVerticalTextOrientation(orientation);
    const lineRuns =
      Array.isArray(highlights) && highlights.length
        ? listLineHighlightRuns({
            text,
            lines: sourceTextLines(text),
            highlights,
            phrases: highlightPhrases,
            orientation: "horizontal",
          })
        : null;

    const reportSelection = (event) => {
      event.stopPropagation();
      const state = readSelectionState(event.currentTarget);
      if (!state) return;
      onSelectionChange?.(state);
    };

    // Do not inherit the parent's lineAdvance: that second strut is what
    // used to blow the edit preview to ~2x canvas spacing.
    const editingRunTypeSx = (run) => {
      const next = {
        ...runTypeSx(run),
        // inline, not inline-block: a run wide enough to wrap re-wraps *inside*
        // an inline-block, packs those lines at its own 1em line-height and then
        // baseline-aligns the last one, so the whole run climbs out of the top
        // of the layer and gets clipped as soon as the text needs two lines.
        display: "inline",
        lineHeight: 1,
        verticalAlign: "baseline",
      };
      // Layer fill hugs each line on a clone-break span, not these runs.
      if (!run?.style?.background) {
        return {
          ...next,
          backgroundColor: "transparent",
          boxShadow: "none",
          padding: 0,
          margin: 0,
          borderRadius: 0,
          ...(Number(glyphFontSize) > 0
            ? { fontSize: `${glyphFontSize}px` }
            : {}),
        };
      }
      return next;
    };

    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          // Visible, not hidden: typing past the fitted size used to clip the
          // text away mid-edit. The refit on commit brings it back inside.
          overflow: "visible",
          ...(vertical
            ? {
                display: "flex",
                alignItems: "center",
                justifyContent:
                  align === "center"
                    ? "center"
                    : align === "right"
                      ? "flex-end"
                      : "flex-start",
              }
            : {}),
          opacity,
        }}
      >
        <Box
          component="div"
          contentEditable
          suppressContentEditableWarning
          autoFocus
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onMouseUp={reportSelection}
          onKeyUp={reportSelection}
          onBlur={(event) => {
            onCommit?.(event.currentTarget.innerText);
            onLeave?.();
          }}
          sx={{
            ...(vertical
              ? { flex: 1, minWidth: 0 }
              : {
                  // Anchored on the fitted block's first baseline instead of
                  // flex-centred: CSS centres the strut box (font ascent+descent
                  // per line) while the paint centres the ink box, so centring
                  // both drifted them apart, and every typed line re-centred the
                  // text under the caret.
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: `calc((100% - ${totalHeight}px) / 2 + ${topOffset}px)`,
                }),
            outline: "none",
            margin: 0,
            padding: 0,
            boxSizing: "border-box",
            whiteSpace: isVerticalTextOrientation(orientation)
              ? "normal"
              : "pre-wrap",
            writingMode: isVerticalTextOrientation(orientation)
              ? "vertical-rl"
              : "horizontal-tb",
            textOrientation: isVerticalTextOrientation(orientation)
              ? "upright"
              : undefined,
            lineHeight: `${lineAdvance}px`,
            pointerEvents: "auto",
            userSelect: "text",
            caretColor: color,
            "& ::selection": {
              backgroundColor: "rgba(37, 99, 235, 0.38)",
            },
            "& span": {
              lineHeight: 1,
              verticalAlign: "baseline",
            },
            "& div, & p": {
              margin: 0,
              padding: 0,
              lineHeight: "inherit",
            },
            ...sharedType,
            ...background,
          }}
        >
          {lineBackground ? (
            <Box component="span" sx={lineBackground}>
              <HighlightedSourceText
                text={text}
                lineRuns={lineRuns}
                runTypeSx={editingRunTypeSx}
                glyphFontSize={glyphFontSize}
              />
            </Box>
          ) : (
            <HighlightedSourceText
              text={text}
              lineRuns={lineRuns}
              runTypeSx={editingRunTypeSx}
              glyphFontSize={glyphFontSize}
            />
          )}
        </Box>
      </Box>
    );
  },
  () => true
);

/**
 * Renders fitted text with the same line metrics as Canvas2D paint
 * (assembleTemplateImage / composeLayers), not CSS unitless line-height.
 * Unitless CSS line-height makes blocks taller than canvas measureBlock,
 * which clips text in the editor while produce bake still looks correct.
 */
const FittedTextPreview = ({
  text,
  layout,
  fallbackFontSize = 12,
  scale = 1,
  color = "#fff",
  fontFamily = "sans-serif",
  align = "left",
  orientation = "horizontal",
  textStyle,
  letterSpacing = 0,
  strokeWidth = 0,
  strokeColor,
  shadow = null,
  backgroundPadding = 0,
  backgroundColor,
  backgroundRadius,
  fontWeight,
  opacity = 1,
  isEditing = false,
  onCommit,
  highlights,
  highlightPhrases,
  inlineReplacements,
  replacementMetrics,
  inlineImages,
  isSelectingText = false,
  selectedPhrase = "",
  onSelectionChange,
}) => {
  const [editBlurred, setEditBlurred] = useState(false);
  useEffect(() => {
    if (!isEditing) setEditBlurred(false);
  }, [isEditing]);
  const unscaledSize = layout?.ok
    ? layout.fontSize
    : Number(fallbackFontSize) > 0
      ? Number(fallbackFontSize)
      : 12;
  const lineHeightMult = layout?.ok
    ? layout.lineHeightMult
    : isVerticalTextOrientation(orientation)
      ? 1
      : 1.2;
  const resolvedLines = layout?.ok
    ? layout.lines
    : isVerticalTextOrientation(orientation)
      ? wrapTextVertical({ text })
      : String(text || "").split(/\n/);
  const lines =
    resolvedLines && resolvedLines.length > 0 ? resolvedLines : [""];

  const unscaledLineAdvance =
    layout?.ok && layout.metrics?.lineAdvance != null
      ? layout.metrics.lineAdvance
      : unscaledSize * lineHeightMult;
  const unscaledFirstAscent =
    layout?.ok && layout.metrics?.firstAscent != null
      ? layout.metrics.firstAscent
      : unscaledSize * 0.78;
  const unscaledLastDescent =
    layout?.ok && layout.metrics?.lastDescent != null
      ? layout.metrics.lastDescent
      : unscaledSize * 0.22;
  const unscaledTotalHeight =
    layout?.ok && layout.metrics?.totalHeight != null
      ? layout.metrics.totalHeight
      : lines.length <= 1
        ? unscaledFirstAscent + unscaledLastDescent
        : (lines.length - 1) * unscaledLineAdvance +
          unscaledFirstAscent +
          unscaledLastDescent;

  const fontSize = unscaledSize * scale;
  const spacing =
    (layout?.ok ? layout.letterSpacing : Number(letterSpacing) || 0) * scale;
  const lineAdvance = unscaledLineAdvance * scale;
  const firstAscent = unscaledFirstAscent * scale;
  const totalHeight = unscaledTotalHeight * scale;

  const emphasis = textEmphasisSx(textStyle);
  const stroke = textStrokeSx({ strokeWidth, strokeColor, scale });
  const background = textBackgroundSx({
    backgroundPadding,
    backgroundColor,
    backgroundRadius,
    scale,
  });
  const lineBackground = textBackgroundSx({
    backgroundPadding,
    backgroundColor,
    backgroundRadius,
    scale,
    hugGlyphs: true,
  });
  const sharedType = {
    color,
    fontFamily: `"${fontFamily}"`,
    fontSize: `${fontSize}px`,
    fontWeight: Number(fontWeight) > 0 ? Number(fontWeight) : 400,
    letterSpacing: `${spacing}px`,
    textAlign: align,
    ...emphasis,
    ...stroke,
    ...textShadowSx({ shadow, scale }),
  };
  const lineJustify =
    align === "center"
      ? "center"
      : align === "right"
        ? "flex-end"
        : "flex-start";
  const hasLineBackground = backgroundPaddingPx(backgroundPadding) > 0;
  // Stroke is centered on the outline, so half of it paints outside the
  // layout box. A line flush with the box (the left of a "V") loses that
  // half to the clip unless the clip opens by the same amount.
  let widestStroke = strokeWidthPx(strokeWidth);
  if (Array.isArray(highlights)) {
    highlights.forEach((slot) => {
      const width = strokeWidthPx(slot?.strokeWidth);
      if (width > widestStroke) widestStroke = width;
    });
  }
  // Half the stroke. Left/right text shifts by this so the outline stays
  // inside the layer, matching the canvas bake. Center already has that
  // room because the fit reserved the full stroke on the line width.
  const strokeInset = (widestStroke * scale) / 2;
  const strokeShift =
    align === "right" ? -strokeInset : align === "center" ? 0 : strokeInset;
  const pillPad = Math.max(
    maxTextBackgroundPadding({ backgroundPadding, highlights }) * scale,
    textShadowInkPad(shadow) * scale,
    strokeInset,
    // Italic glyphs lean past their advance box.
    isItalicTextStyle(textStyle) ? fontSize * 0.25 : 0
  );
  const strut = measureFontStrut({
    fontSize,
    fontFamily,
    italic: isItalicTextStyle(textStyle),
    fontWeight: Number(fontWeight) > 0 ? Number(fontWeight) : 400,
  });
  const strutHeight = strut.ascent + strut.descent;
  const probeFont = {
    fontFamily,
    fontSize,
    fontWeight: Number(fontWeight) > 0 ? Number(fontWeight) : 400,
    italic: isItalicTextStyle(textStyle),
  };
  // Baseline of a 1em line box as the browser lays it out for this font. It is
  // not a constant share of the em (≈0.86 for Archivo Black, ≈0.9 for Barlow
  // Condensed), and lifting each line by exactly this much is what puts its
  // baseline on firstAscent, where canvas produce paints it.
  const oneEmBaseline = measureCssFirstBaseline({
    ...probeFont,
    lineAdvance: fontSize,
    fallback: (fontSize - strutHeight) / 2 + strut.ascent,
  });
  const lineShift = `translateY(-${oneEmBaseline}px)`;
  // The pill is the same 1em box, but produce paints it from a fixed share of
  // the em above the baseline, so it is lifted by that instead.
  const pillShift = `translateY(-${fontSize * PILL_ASCENT_EM}px)`;
  const showLivePaint = !isEditing || editBlurred;
  const hasInlineReplacements =
    listActiveInlineReplacements(inlineReplacements, replacementMetrics)
      .length > 0;
  const lineRuns =
    showLivePaint &&
    ((Array.isArray(highlights) && highlights.length) ||
      selectedPhrase ||
      hasInlineReplacements)
      ? listLineInlineRuns({
          text,
          lines,
          highlights,
          phrases: highlightPhrases,
          replacements: inlineReplacements,
          metrics: replacementMetrics,
          orientation,
          selectionPhrase: selectedPhrase,
        })
      : null;
  const capHeightPx = fontSize * INLINE_IMAGE_CAP_EM;

  const runBackgroundInput = (run) => {
    if (isInlineImageRun(run)) {
      return {
        backgroundPadding: 0,
        backgroundColor,
        backgroundRadius,
      };
    }
    const style = run?.style || {};
    return {
      backgroundPadding:
        style.background != null
          ? style.backgroundPadding
          : hasLineBackground
            ? backgroundPadding
            : 0,
      backgroundColor: style.background || backgroundColor,
      backgroundRadius:
        style.background != null && style.backgroundRadius != null
          ? style.backgroundRadius
          : backgroundRadius,
    };
  };

  const runBackgroundKey = (run) => {
    const input = runBackgroundInput(run);
    if (backgroundPaddingPx(input.backgroundPadding) <= 0) return "";
    return [
      input.backgroundColor || "",
      input.backgroundPadding ?? "",
      input.backgroundRadius ?? "",
    ].join("|");
  };

  // Canvas produce paints one roundRect per run; opaque overlap reads as a
  // single capsule. CSS cannot do that: each span keeps its own radius, and
  // an absolutely positioned inline layer can wrap, stacking the pills.
  const groupRunsByBackground = (runs = []) => {
    const groups = [];
    runs.forEach((run) => {
      const key = runBackgroundKey(run);
      const last = groups[groups.length - 1];
      if (key && last && last.key === key) {
        last.runs.push(run);
        return;
      }
      groups.push({ key, runs: [run] });
    });
    return groups;
  };

  const runTypeSx = (run, paint = "all") => {
    const style = run?.style || {};
    const runStroke = textStrokeSx({
      strokeWidth: style.stroke != null ? style.strokeWidth : strokeWidth,
      strokeColor: style.stroke || strokeColor,
      scale,
    });
    const runBackground = textBackgroundSx({
      ...runBackgroundInput(run),
      scale,
      hugGlyphs: true,
    });
    const next = {
      ...sharedType,
      // inline-block, not inline: the background area of an inline box comes
      // from the font's ascent+descent rather than from line-height, so it
      // outgrows lineAdvance and bleeds over the neighbouring lines, and the
      // negative margin hugGlyphs adds cannot pull it back because vertical
      // margins do not apply to inline boxes. As a block box it measures
      // exactly 1em, matching the non-run branch below.
      display: "inline-block",
      flexShrink: 0,
      verticalAlign: "baseline",
      ...(style.fill ? { color: style.fill } : {}),
      ...(style.fontFamily ? { fontFamily: `"${style.fontFamily}"` } : {}),
      ...(Number(style.fontWeight) > 0
        ? { fontWeight: Number(style.fontWeight) }
        : {}),
      ...(style.textStyle ? textEmphasisSx(style.textStyle) : {}),
      ...runStroke,
      ...runBackground,
      ...(style.selected
        ? {
            boxShadow: "inset 0 0 0 2px rgba(37, 99, 235, 0.95)",
            ...(style.background
              ? {}
              : { backgroundColor: "rgba(37, 99, 235, 0.38)" }),
          }
        : {}),
    };
    if (paint === "background") {
      return {
        ...next,
        color: "transparent",
        WebkitTextStroke: "0px transparent",
        textShadow: "none",
        textDecoration: "none",
        boxShadow: "none",
      };
    }
    if (paint === "glyphs") {
      return {
        ...next,
        backgroundColor: "transparent",
      };
    }
    return next;
  };

  const highlightRowSx = {
    // inline-flex, not flex: a block flex chip inside the inline-block line
    // drops out of the glyph flow, so the pill stays at the line start while
    // the word walks right on wrap/font-size changes.
    display: "inline-flex",
    flexWrap: "nowrap",
    alignItems: "baseline",
    whiteSpace: "pre",
    width: "max-content",
  };

  const paintInlineImage = (run, key, paint) => {
    const size = {
      height: capHeightPx,
      width: capHeightPx * (Number(run.aspect) > 0 ? Number(run.aspect) : 0),
    };
    const url = inlineImages?.[run.assetRef];
    if (url && size.width > 0) {
      return (
        <Box
          key={key}
          component="img"
          src={url}
          alt=""
          sx={{
            height: `${size.height}px`,
            width: `${size.width}px`,
            display: "inline-block",
            verticalAlign: "baseline",
            flexShrink: 0,
            objectFit: "contain",
            visibility: paint === "background" ? "hidden" : "visible",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      );
    }
    return (
      <GlyphSpan key={key} sx={runTypeSx(run, paint)} paint={paint}>
        {run.text || run.phrase || "\u00A0"}
      </GlyphSpan>
    );
  };

  const paintRunSpans = (runs, paint, keyPrefix) =>
    runs.map((run, runIndex) =>
      isInlineImageRun(run) ? (
        paintInlineImage(run, `${keyPrefix}-${runIndex}`, paint)
      ) : (
        <GlyphSpan
          key={`${keyPrefix}-${runIndex}`}
          sx={runTypeSx(run, paint)}
          paint={paint}
        >
          {run.text || "\u00A0"}
        </GlyphSpan>
      )
    );

  const paintBackgroundGroups = (runs, keyPrefix) =>
    groupRunsByBackground(runs).map((group, groupIndex) => {
      const inner = group.runs.map((run, runIndex) =>
        isInlineImageRun(run) ? (
          paintInlineImage(
            run,
            `${keyPrefix}-${groupIndex}-${runIndex}`,
            "background"
          )
        ) : (
          <Box
            key={`${keyPrefix}-${groupIndex}-${runIndex}`}
            component="span"
            sx={{
              ...runTypeSx(run, "background"),
              backgroundColor: "transparent",
              padding: 0,
              margin: 0,
              borderRadius: 0,
            }}
          >
            {run.text || "\u00A0"}
          </Box>
        )
      );
      if (!group.key) {
        return (
          <Fragment key={`${keyPrefix}-gap-${groupIndex}`}>{inner}</Fragment>
        );
      }
      return (
        <Box
          key={`${keyPrefix}-chip-${groupIndex}`}
          sx={{
            ...highlightRowSx,
            flexShrink: 0,
            ...textBackgroundSx({
              ...runBackgroundInput(group.runs[0]),
              scale,
              hugGlyphs: true,
            }),
          }}
        >
          {inner}
        </Box>
      );
    });

  const lineTypeSx = {
    // inline-block (not inline): translateY is ignored on inline boxes, so the
    // line stayed parked at firstAscent — the bottom of the centered block.
    display: "inline-block",
    lineHeight: 1,
    transform: lineShift,
    whiteSpace: "pre",
    margin: 0,
    padding: 0,
    ...sharedType,
  };
  const lineRowSx = {
    ...lineTypeSx,
    display: "inline-flex",
    flexWrap: "nowrap",
    alignItems: "baseline",
  };
  // Placed with a transform, not `top`: Chrome rounds the layout offset of text
  // to whole CSS pixels of the nearest transformed ancestor (the zoomed canvas,
  // or the rotated layer), which moved the glyphs up to half a pixel off the
  // fitted baseline — 2.5px on screen at 500%. A translation is applied as is.
  const lineShellSx = (index) => ({
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    transform: `translateY(${firstAscent + index * lineAdvance}px)`,
    display: "flex",
    justifyContent: lineJustify,
  });
  const paintLineBackground = (line, index) => {
    const runs = lineRuns?.[index];
    if (runs) {
      return (
        <Box key={`bg-line-${index}`} aria-hidden sx={lineShellSx(index)}>
          <Box sx={{ ...lineRowSx, transform: pillShift }}>
            {paintBackgroundGroups(runs, `bg-${index}`)}
          </Box>
        </Box>
      );
    }
    if (!hasLineBackground) return null;
    return (
      <Box key={`bg-plain-${index}`} aria-hidden sx={lineShellSx(index)}>
        <Box
          sx={{
            ...lineTypeSx,
            transform: pillShift,
            ...lineBackground,
            color: "transparent",
            WebkitTextStroke: "0px transparent",
            textDecoration: "none",
          }}
        >
          {line || "\u00A0"}
        </Box>
      </Box>
    );
  };
  const paintLineGlyphs = (line, index) => {
    const runs = lineRuns?.[index];
    if (runs) {
      return (
        <Box key={`gl-line-${index}`} sx={lineShellSx(index)}>
          <Box sx={lineRowSx}>
            {paintRunSpans(runs, "glyphs", `gl-${index}`)}
          </Box>
        </Box>
      );
    }
    if (!hasLineBackground) {
      return (
        <Box key={`gl-plain-${index}`} sx={lineShellSx(index)}>
          <GlyphSpan sx={lineTypeSx}>{line || "\u00A0"}</GlyphSpan>
        </Box>
      );
    }
    return (
      <Box key={`gl-plain-${index}`} sx={lineShellSx(index)}>
        <Box
          sx={{
            ...lineTypeSx,
            ...lineBackground,
            backgroundColor: "transparent",
            WebkitTextStroke: "0px transparent",
          }}
        >
          <GlyphSpan sx={{ ...lineTypeSx, transform: "none" }}>
            {line || "\u00A0"}
          </GlyphSpan>
        </Box>
      </Box>
    );
  };

  const onPointerDownHighlight = (event) => {
    if (!isSelectingText && !isEditing) return;
    event.stopPropagation();
  };

  const onMouseUpHighlight = (event) => {
    if (!isSelectingText && !isEditing) return;
    event.stopPropagation();
    const state = readSelectionState(event.currentTarget);
    if (!state) return;
    // The read-only paint has no caret, and its offsets would be off by the
    // line breaks the wrap introduced, so only the phrase is reported.
    if (state.kind !== "range") {
      onSelectionChange?.({ kind: "none" });
      return;
    }
    const phrase = isVerticalTextOrientation(orientation)
      ? expandCompactPhraseInText({ text, phrase: state.phrase }) ||
        state.phrase
      : state.phrase;
    onSelectionChange?.({ kind: "range", phrase });
  };

  const onReenterEdit = (event) => {
    if (!isEditing || !editBlurred) return;
    event.stopPropagation();
    setEditBlurred(false);
  };

  if (isEditing && !editBlurred) {
    const editUsesLineHug =
      !isVerticalTextOrientation(orientation) && hasLineBackground;
    const cssFirstBaseline = measureCssFirstBaseline({
      ...probeFont,
      lineAdvance,
      fallback: (lineAdvance - strutHeight) / 2 + strut.ascent,
    });
    // The fitted paint lifts each 1em line box by its own baseline (lineShift),
    // so its first baseline lands right on firstAscent.
    const paintFirstBaseline = firstAscent;
    const pillAscent = fontSize * PILL_ASCENT_EM;
    // The read-only pill is a 1em box (inline-block at line-height 1), but the
    // background of an inline fragment covers the font's content area — taller
    // than 1em by the leading the font declares, which is what made the pill
    // grow on entering edit. Shrinking the pill span's own font squeezes that
    // area back to 1em around the same baseline; EditGlyphs restores the real
    // size for the text, so nothing else moves.
    const pillFontSize =
      strutHeight > 0 ? (fontSize * fontSize) / strutHeight : fontSize;
    // A probe answers `lineHeight / 2 + (ascent - descent) / 2`, so two knowns
    // (its answer and the content area) pin the ascent down. Squeezed to 1em
    // the pill keeps the font's ascent/descent ratio, which is not where the
    // read-only paint lifts its pill (pillShift); the shadow offset moves it.
    const cssAscent = (strutHeight + 2 * oneEmBaseline - fontSize) / 2;
    const pillOffsetY =
      strutHeight > 0 ? (cssAscent * fontSize) / strutHeight - pillAscent : 0;
    // Pills painted with a spread shadow keep the glyph advance the fitted
    // layout measured, so the edit preview wraps where the bake wraps.
    const pillSx = (input) => {
      const pill = textBackgroundSx({
        ...input,
        scale,
        spread: true,
        spreadOffsetY: Math.round(pillOffsetY * 100) / 100,
      });
      if (!Object.keys(pill).length) return pill;
      return { ...pill, fontSize: `${pillFontSize}px` };
    };
    const editLineBackground = pillSx({
      backgroundPadding,
      backgroundColor,
      backgroundRadius,
    });
    const editRunTypeSx = (run) => {
      const base = runTypeSx(run);
      const pill = pillSx(runBackgroundInput(run));
      if (!Object.keys(pill).length) return base;
      return { ...base, padding: 0, margin: 0, ...pill };
    };
    return (
      <EditingFittedText
        text={text}
        highlights={highlights}
        highlightPhrases={highlightPhrases}
        orientation={orientation}
        align={align}
        sharedType={sharedType}
        background={editUsesLineHug ? {} : background}
        lineBackground={editUsesLineHug ? editLineBackground : undefined}
        lineAdvance={lineAdvance}
        totalHeight={totalHeight}
        topOffset={paintFirstBaseline - cssFirstBaseline}
        glyphFontSize={fontSize}
        color={color}
        opacity={opacity}
        runTypeSx={editRunTypeSx}
        onCommit={onCommit}
        onSelectionChange={onSelectionChange}
        onLeave={() => setEditBlurred(true)}
      />
    );
  }

  if (isVerticalTextOrientation(orientation)) {
    const stacked = isVerticalUpTextOrientation(orientation)
      ? "column-reverse"
      : "column";
    return (
      <Box
        onPointerDown={onPointerDownHighlight}
        onMouseUp={onMouseUpHighlight}
        onDoubleClick={onReenterEdit}
        sx={{
          width: "100%",
          height: "100%",
          overflow: "visible",
          clipPath: `inset(-${pillPad}px)`,
          display: "flex",
          flexDirection: stacked,
          alignItems: "center",
          justifyContent: "center",
          whiteSpace: "pre",
          lineHeight: `${lineAdvance}px`,
          opacity,
          pointerEvents: isSelectingText || isEditing ? "auto" : undefined,
          userSelect: isSelectingText || isEditing ? "text" : undefined,
          cursor: isSelectingText || isEditing ? "text" : undefined,
          "& ::selection": {
            backgroundColor: "rgba(37, 99, 235, 0.38)",
          },
          ...sharedType,
          ...background,
        }}
      >
        {lineRuns ? (
          <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: stacked,
                alignItems: "center",
                justifyContent: "center",
                zIndex: 0,
                pointerEvents: "none",
                userSelect: "none",
                whiteSpace: "pre",
                lineHeight: `${lineAdvance}px`,
              }}
            >
              {lineRuns.map((runs, index) => (
                <Box
                  key={`vbg-${index}`}
                  component="span"
                  sx={{
                    ...runTypeSx(runs[0], "background"),
                    display: "block",
                    flexShrink: 0,
                    height: `${lineAdvance}px`,
                    lineHeight: `${lineAdvance}px`,
                  }}
                >
                  {runs[0]?.text || ""}
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: stacked,
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                whiteSpace: "pre",
                lineHeight: `${lineAdvance}px`,
              }}
            >
              {lineRuns.map((runs, index) => (
                <GlyphSpan
                  key={`vrun-${index}`}
                  paint="glyphs"
                  sx={{
                    ...runTypeSx(runs[0], "glyphs"),
                    display: "block",
                    flexShrink: 0,
                    height: `${lineAdvance}px`,
                    lineHeight: `${lineAdvance}px`,
                  }}
                >
                  {runs[0]?.text || ""}
                </GlyphSpan>
              ))}
            </Box>
          </Box>
        ) : (
          lines.map((line, index) => (
            <Box
              key={`vline-${index}`}
              component="span"
              sx={{
                display: "block",
                flexShrink: 0,
                height: `${lineAdvance}px`,
                lineHeight: `${lineAdvance}px`,
              }}
            >
              {line || "\u00A0"}
            </Box>
          ))
        )}
      </Box>
    );
  }

  return (
    <Box
      onPointerDown={onPointerDownHighlight}
      onMouseUp={onMouseUpHighlight}
      onDoubleClick={onReenterEdit}
      sx={{
        width: "100%",
        height: "100%",
        // Pill, shadow and stroke hug `pillPad` past the glyphs, so a line flush
        // against the layer box paints outside it. Clipping at the box would
        // slice that ink; the clip is opened up by exactly that much, and the
        // glyphs themselves are still bound to the box as before.
        overflow: "visible",
        clipPath: `inset(-${pillPad}px)`,
        position: "relative",
        opacity,
        pointerEvents: isSelectingText || isEditing ? "auto" : undefined,
        userSelect: isSelectingText || isEditing ? "text" : undefined,
        cursor: isSelectingText || isEditing ? "text" : undefined,
        "& ::selection": {
          backgroundColor: "rgba(37, 99, 235, 0.38)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          // Centred by a transform for the same reason as lineShellSx. The box
          // spans the whole layer so that 50% is half the layer's height.
          height: "100%",
          transform: `translate(${strokeShift}px, calc(50% - ${totalHeight / 2}px))`,
          pointerEvents: isSelectingText || isEditing ? "auto" : "none",
          userSelect: isSelectingText || isEditing ? "text" : undefined,
          cursor: isSelectingText || isEditing ? "text" : undefined,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {lines.map(paintLineBackground)}
        </Box>
        <Box sx={{ position: "relative", zIndex: 1, height: "100%" }}>
          {lines.map(paintLineGlyphs)}
        </Box>
      </Box>
    </Box>
  );
};

export default FittedTextPreview;
